import React from 'react';
import PropTypes from 'prop-types';
import {
  Page,
  View,
  Text,
} from '@react-pdf/renderer';

import TablePageHeader from '../../components/TablePageHeader';
import BottomPagination from '../../components/BottomPagination';
import DraftOverlay from '../../components/DraftOverlay';

import { commaEvery3rdChar } from '../../../../../../utils';

export default function EquityValue({ reportData, exhibitNumber, pageNumber = 5 }) {
  const tableData = reportData.EquityValueConclusionSummary;

  const tableHeaders = [
    'Valuation approach',
    ...tableData.backsolve ? [tableData.backsolve['Valuation approach']] : [],
    ...tableData.pubco ? [tableData.pubco['Valuation approach']] : [],
    ...tableData.mna ? [tableData.mna['Valuation approach']] : [],
    'Concluded',
  ];

  const EV_KEY = 'Enterprise value';
  const EV_KEY_LEGACY = 'Enterprise value, minority, marketable';

  const getEvValue = (methodData) => methodData?.[EV_KEY] ?? methodData?.[EV_KEY_LEGACY];

  const parseWeight = (weight) => {
    const n = parseFloat(String(weight ?? 0).replace('%', ''));
    return Number.isFinite(n) ? n : 0;
  };

  const formatMoney = (val) => {
    if (val == null || val === '' || val === '-') return '$0';
    const n = Number(val);
    if (!Number.isFinite(n)) return '$0';
    return `$${commaEvery3rdChar(Math.round(n).toString())}`;
  };

  const methodEquity = (key) => {
    const method = tableData[key];
    if (!method || method['Equity value'] == null || method['Equity value'] === '') return null;
    const n = Number(method['Equity value']);
    return Number.isFinite(n) ? n : null;
  };

  const methodCash = (key) => Number(tableData[key]?.['Plus: accrued interest, cash, and equivalents'] || 0) || 0;
  const methodDebt = (key) => Number(tableData[key]?.['Less: debt'] || 0) || 0;

  const backsolveWeight = parseWeight(reportData.weights?.backsolve);
  const pubcoWeight = parseWeight(reportData.weights?.pubco);
  const mnaWeight = parseWeight(reportData.weights?.mna);
  const backsolveEq = methodEquity('goalSeek') ?? methodEquity('backsolve');
  const pubcoEq = methodEquity('pubco') ?? methodEquity('pubCo');
  const mnaEq = methodEquity('mna');

  const concludedEquity = (() => {
    let total = 0;
    let weight = 0;
    if (backsolveWeight && backsolveEq != null) {
      total += backsolveEq * (backsolveWeight / 100);
      weight += backsolveWeight;
    }
    if (pubcoWeight && pubcoEq != null) {
      total += pubcoEq * (pubcoWeight / 100);
      weight += pubcoWeight;
    }
    if (mnaWeight && mnaEq != null) {
      total += mnaEq * (mnaWeight / 100);
      weight += mnaWeight;
    }
    if (weight > 0) return total;
    return pubcoEq ?? backsolveEq ?? mnaEq;
  })();

  const cashValue = methodCash('pubco') || methodCash('goalSeek') || methodCash('backsolve');
  const debtValue = methodDebt('pubco') || methodDebt('goalSeek') || methodDebt('backsolve');
  const concludedEv = concludedEquity != null ? concludedEquity - cashValue + debtValue : null;

  const rowNames = [
    EV_KEY,
    'Plus: cash and equivalents',
    'Less: debt',
    'Equity value',
  ];

  const firstColumnWidth = () => {
    if (tableHeaders.length === 5) return '380px';
    if (tableHeaders.length === 4) return '560px';
    if (tableHeaders.length === 3) return '740px';
    return '920px';
  };

  function returnTableData(columnName, rowName) {
    let goalSeekEqValue;
    let backsolveTrueEqValue;
    let cash;
    let debt;

    if (tableData.goalSeek) {
      goalSeekEqValue = getEvValue(tableData.goalSeek);
      backsolveTrueEqValue = tableData.goalSeek['Equity value'];
      cash = tableData.goalSeek['Plus: accrued interest, cash, and equivalents'];
      debt = tableData.goalSeek['Less: debt'];
    } else if (tableData.backsolve) {
      goalSeekEqValue = getEvValue(tableData.backsolve);
      backsolveTrueEqValue = tableData.backsolve['Equity value'];
      cash = tableData.backsolve['Plus: accrued interest, cash, and equivalents'];
      debt = tableData.backsolve['Less: debt'];
    }

    if (columnName === 'Concluded') {
      if (rowName === EV_KEY) return formatMoney(concludedEv);
      if (rowName === 'Plus: cash and equivalents') return formatMoney(cashValue);
      if (rowName === 'Less: debt') return formatMoney(debtValue);
      if (rowName === 'Equity value') return formatMoney(concludedEquity);
      return '-';
    }

    const currentMethod = Object.keys(tableData).find((method) => tableData[method]['Valuation approach'] === columnName);

    if (columnName === 'Backsolve Method - Option Pricing Model') {
      if (rowName === EV_KEY) return formatMoney(goalSeekEqValue);
      if (rowName === 'Plus: cash and equivalents') return formatMoney(cash);
      if (rowName === 'Less: debt') return formatMoney(debt);
      if (rowName === 'Equity value') return formatMoney(backsolveTrueEqValue);
      return '-';
    }

    if (columnName === 'Guideline Public Company Method') {
      if (rowName === EV_KEY) return formatMoney(getEvValue(tableData[currentMethod]));
      if (rowName === 'Weighting') return `${tableData[currentMethod][rowName]}%`;
      if (rowName === 'Plus: cash and equivalents') return formatMoney(tableData[currentMethod]?.['Plus: accrued interest, cash, and equivalents']);
      if (rowName === 'Less: debt') return formatMoney(tableData[currentMethod]?.['Less: debt']);
      if (rowName === 'Equity value') return formatMoney(tableData[currentMethod]?.['Equity value']);
    }
    return '-';
  }

  function adjustedEquityValue(columnName) {
    if (columnName === 'Concluded') return formatMoney(concludedEquity);
    if (columnName === 'Guideline Public Company Method') return formatMoney(pubcoEq);
    const currentMethod = Object.keys(tableData).find((method) => tableData[method]['Valuation approach'] === columnName);
    if (!currentMethod || !tableData[currentMethod]?.['Equity value']) return '';
    return formatMoney(tableData[currentMethod]['Equity value']);
  }

  const footnote = reportData.footnoteData.eqValueConclusion;

  return (
    <Page
      size="TABLOID"
      orientation="landscape"
      style={{ position: 'relative', backgroundColor: '#EAEAEB' }}
    >
      <TablePageHeader
        companyName={reportData.companyInfo.companyName}
        pageName={`Equity Value Conclusion & Summary   |   Exhibit ${exhibitNumber}`}
      />
      <View
        style={{
          marginTop: '84px',
          marginLeft: '80px',
          display: 'flex',
          flexDirection: 'column',
          width: '1064px',
          border: '1px',
          borderColor: '#AFB1B3',
        }}
      >
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            height: '56px',
            backgroundColor: '#0C2617',
            borderBottom: '1px',
            borderColor: '#CCCCCC',
          }}
        >
          <Text
            style={{
              fontFamily: 'RobotoMed',
              fontSize: '14px',
              color: '#8AB9A4',
              marginTop: 'auto',
              paddingBottom: '8px',
              paddingLeft: '16px',
              letterSpacing: '0.08px',
              width: '740px',
            }}
          >
            (USD$)
          </Text>
          <Text
            style={{
              fontFamily: 'RobotoMed',
              fontSize: '14px',
              color: '#E1E2EC',
              marginTop: 'auto',
              paddingBottom: '8px',
              paddingLeft: '16px',
              paddingRight: '16px',
              letterSpacing: '0.08px',
              width: '290px',
              textAlign: 'right',
            }}
          >
            Market
          </Text>
        </View>
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            height: '56px',
            backgroundColor: '#307157',
            borderBottom: '1px',
            borderColor: '#CCCCCC',
          }}
        >
          {tableHeaders.map((dataName, index) => (
            <Text
              key={dataName}
              style={{
                fontFamily: 'RobotoMed',
                fontSize: '12px',
                color: '#E1E2EC',
                width: index === 0 ? firstColumnWidth() : '180px',
                marginTop: 'auto',
                paddingBottom: '4px',
                paddingLeft: '16px',
                paddingRight: '16px',
                letterSpacing: '0.5px',
                textAlign: index !== 0 && 'right',
              }}
            >
              {dataName}
            </Text>
          ))}
        </View>
        {rowNames.map((rowName, rowIndex) => (
          <View
            key={rowName}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              height: '32px',
              backgroundColor: rowIndex % 2 === 0 ? '#F0F0F3' : '#FAFAFD',
              ...rowIndex === 3 && { borderBottom: '1px', borderTop: '1px', borderColor: '#999BA5' },
            }}
          >
            {tableHeaders.map((dataName, cellIndex) => cellIndex === 0 ? (
              <Text
                key={dataName}
                style={{
                  fontFamily: rowIndex === 3 ? 'RobotoMed' : 'Roboto',
                  fontSize: '12px',
                  color: '#49454F',
                  width: firstColumnWidth(),
                  paddingLeft: '16px',
                  letterSpacing: '0.5px',
                }}
              >
                {rowName}
              </Text>
            ) : (
              <Text
                key={dataName}
                style={{
                  fontFamily: 'Roboto',
                  fontSize: '12px',
                  color: '#49454F',
                  width: '180px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  letterSpacing: '0.5px',
                  textAlign: 'right',
                }}
              >
                {returnTableData(dataName, rowName)}
              </Text>
            ))}
          </View>
        ))}
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            height: '32px',
            backgroundColor: '#FAFAFD',
          }}
        >
          {tableHeaders.map((dataName, columnIndex) => (
            <Text
              key={dataName}
              style={{
                fontFamily: 'Roboto',
                fontSize: '12px',
                color: '#49454F',
                width: columnIndex === 0 ? firstColumnWidth() : '180px',
                paddingLeft: '16px',
                ...columnIndex !== 0 && { paddingRight: '16px', textAlign: 'right' },
                letterSpacing: '0.5px',
              }}
            >
              {columnIndex === 0 ?
                'Market equity adjustment' :
                dataName === 'Backsolve Method - Option Pricing Model' ?
                  `${tableData.marketAdjustment.toFixed(0)}%` : ''}
            </Text>
          ))}
        </View>
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            height: '32px',
            backgroundColor: '#9ECEBB',
          }}
        >
          {tableHeaders.map((dataName, columnIndex) => (
            <Text
              key={dataName}
              style={{
                fontFamily: 'RobotoMed',
                fontSize: '12px',
                color: '#49454F',
                width: columnIndex === 0 ? firstColumnWidth() : '180px',
                paddingLeft: '16px',
                ...columnIndex !== 0 && { paddingRight: '16px', textAlign: 'right' },
                letterSpacing: '0.5px',
              }}
            >
              {columnIndex === 0 ? 'Adjusted equity value' : adjustedEquityValue(dataName, columnIndex)}
            </Text>
          ))}
        </View>
      </View>
      {!!footnote && (
        <Text
          style={{
            fontFamily: 'Roboto',
            fontSize: '8px',
            color: '#49454F',
            letterSpacing: '0.4px',
            marginTop: '16px',
            marginLeft: '90px',
            width: '450px',
            lineHeight: '0.55mm',
          }}
        >
          Footnote:&nbsp;
          {footnote}
        </Text>
      )}
      <BottomPagination
        pageNumber={pageNumber}
        reportData={reportData}
      />
      {reportData.isDraft && <DraftOverlay />}
    </Page>
  );
}

EquityValue.propTypes = {
  reportData: PropTypes.object.isRequired,
  exhibitNumber: PropTypes.number.isRequired,
  pageNumber: PropTypes.number,
};
